'use client'

// React Imports
import { useEffect, useState } from 'react'

// Next Imports
import Link from 'next/link'
import { useRouter, useSearchParams } from 'next/navigation'

// Third-party Imports
import { useTranslate } from '@refinedev/core'
import { zodResolver } from '@hookform/resolvers/zod'
import { useLogin, useParsed } from '@refinedev/core'
import { useForm } from 'react-hook-form'

// Type Imports
import type { SignInValues } from '../auth-schemas'

// Component Imports
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import AuthLayout from '../auth-layout'

// Schema Imports
import { looksLikeEmployeeCode, signInSchema } from '../auth-schemas'

// Util Imports
import { isQsToKey } from '@/lib/safe-next'
import { loginFailureMessage, REDIRECT_GRACE_MS } from '@/lib/login-result'

type LoginVariables = Pick<SignInValues, 'identifier' | 'password'> & { next?: string }

const SignInView = () => {
  const t = useTranslate()
  const { mutate: login, isPending } = useLogin<LoginVariables>()

  /*
   * ! ecollege-ийн засвар: амжилттай нэвтэрсний дараа Refine шилжих хооронд (dev-д удаан) товч
   * ! дахин идэвхждэг байсан тул хэрэглэгч дахин дарж / reload хийдэг байв. Шилжтэл түгжинэ.
   */
  const [redirecting, setRedirecting] = useState(false)

  useEffect(() => {
    if (!redirecting) return

    const timer = setTimeout(() => setRedirecting(false), REDIRECT_GRACE_MS)

    return () => clearTimeout(timer)
  }, [redirecting])

  /*
   * ! ПРОКСИ `?next=`-ЭЭР ХААШАА ОЧИХЫГ ХЭЛНЭ. Нэвтрээгүй хүн `/erp/orders`
   * ! рүү орохыг оролдвол `proxy.ts` түүнийг `/sign-in?next=/erp/orders` рүү
   * ! илгээдэг. Үүнийг цааш дамжуулахгүй бол хэрэглэгч дарсан холбоосдоо
   * ! хэзээ ч хүрэхгүй, үргэлж нүүр хуудас руу унана.
   */
  const searchParams = useSearchParams()
  const router = useRouter()
  const next = searchParams.get('next') ?? undefined

  /*
   * ! 🔴 REFINE-ИЙН `?to=` ШАЛГАЛТЫГ ТОЙРДОГ (FE-02). `useLogin` амжилттай
   * ! нэвтэрсний дараа `?to=`-г провайдерын `redirectTo`-оос ТҮРҮҮЛЖ,
   * ! шалгалтгүй `router.replace` хийдэг (`@refinedev/core` useLogin) ⇒
   * ! `/sign-in?to=https://evil.example` жинхэнэ нэвтрэлтийн дараа гадаад
   * ! сайт руу аваачна. ҮНДСЭН хаалт нь `refine-provider.tsx`-ийн
   * ! `withSafeReturnTo` — `useParsed().params.to` аль хэдийн шүүгдсэн.
   *
   * ! Энд: апп өөрөө `to` үүсгэдэггүй (AuthGate `next` тавина) тул ирсэн
   * ! `to`-г `next` болгоод хаягнаас ХАСНА, хасагдтал «Нэвтрэх» идэвхгүй.
   * ! `get('to')` БИШ — qs `to[]`, `to[0]`, `[to]`-г ч `to` болгодог
   * ! (`isQsToKey`), `get('to')` тэдэнд `null` өгч шалгалт ажилладаггүй байв.
   */
  const { params: parsedParams } = useParsed()
  const parsedTo: string | undefined = parsedParams?.to
  const hasTo = [...searchParams.keys()].some(isQsToKey)

  useEffect(() => {
    if (!hasTo) return

    const params = new URLSearchParams()

    for (const [key, value] of searchParams) {
      if (!isQsToKey(key)) params.append(key, value)
    }

    if (!params.has('next') && parsedTo && parsedTo !== '/') params.set('next', parsedTo)

    const query = params.toString()

    router.replace(query ? `/sign-in?${query}` : '/sign-in')
  }, [hasTo, parsedTo, router, searchParams])

  const form = useForm<SignInValues>({
    resolver: zodResolver(signInSchema),
    defaultValues: { identifier: '', password: '', rememberMe: false }
  })

  /*
   * ! ХОЁР ДАХЬ ТАЛБАРЫН ШОШГО БИЧСЭНЭЭР СОЛИГДОНО. 8 оронтой цифр бол
   * ! ажилтны код тул доорх талбар нь PIN. Эс бөгөөс ажилтан «Нууц үг»
   * ! гэсэн талбарт PIN-ээ бичих эсэхээ мэдэхгүй эргэлзэнэ — хуучин
   * ! системд ч энэ хоёр тусдаа хуудас байсныг нэгтгэхдээ яг үүнийг
   * ! шийдсэн (`UnifiedLoginController`).
   */
  const identifier = form.watch('identifier')
  const isCode = looksLikeEmployeeCode(identifier ?? '')

  const onSubmit = ({ identifier: value, password }: SignInValues) => {
    if (isPending || redirecting || hasTo) return

    login(
      { identifier: value, password, next },
      {
        // ! Refine-ийн login `{ success: false }` буцаадаг (шиддэггүй) тул алдаа onSuccess-д ирнэ.
        onSuccess: result => {
          const message = loginFailureMessage(result)

          if (message) form.setError('password', { message })
          else setRedirecting(true)
        },

        // The API answers a bad identifier and a bad secret identically, so the
        // message lands on the password field rather than naming which was wrong.
        onError: error => form.setError('password', { message: error.message })
      }
    )
  }

  return (
    <AuthLayout
      wide
      title='Нэвтрэх'
      subtitle={t('auth.signInSubtitle')}
      footer={
        <>
          Нууц үгээ мартсан уу?{' '}
          <Button
            variant='link'
            nativeButton={false}
            className='h-auto p-0 text-sm'
            render={<Link href='/forgot-password' />}
          >
            Сэргээх
          </Button>
        </>
      }
    >
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} noValidate className='flex flex-col gap-4'>
          <FormField
            control={form.control}
            name='identifier'
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('auth.identifier')}</FormLabel>
                <FormControl>
                  {/*
                    ! `type='email'` БИШ. Тэр нь хөтчийн шалгалтыг асааж,
                    ! ажилтны кодыг «буруу и-мэйл» гэж тэмдэглэнэ.
                    ! `inputMode` нь утсан дээр код бичихэд тоон гар гаргана.
                  */}
                  <Input
                    type='text'
                    autoComplete='username'
                    inputMode={isCode ? 'numeric' : 'email'}
                    placeholder={t('auth.identifierPlaceholder')}
                    {...field}
                  />
                </FormControl>
                <p className='text-muted-foreground text-xs'>{t('auth.identifierHint')}</p>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name='password'
            render={({ field }) => (
              <FormItem>
                <div className='flex items-center justify-between gap-2'>
                  <FormLabel>{t(isCode ? 'auth.pin' : 'auth.password')}</FormLabel>
                  <Button
                    variant='link'
                    nativeButton={false}
                    className='h-auto p-0 text-xs font-normal'
                    render={<Link href='/forgot-password' />}
                  >
                    Мартсан уу?
                  </Button>
                </div>
                <FormControl>
                  <Input
                    type='password'
                    autoComplete='current-password'
                    inputMode={isCode ? 'numeric' : 'text'}
                    {...field}
                  />
                </FormControl>
                {/*
                  ! ТҮГЖЭЭНИЙ ТАЙЛБАР — ЗӨВХӨН КОД + PIN ҮЕД, ТОГТМОЛ ТЕКСТ. Сервер буруу PIN, түгжээ, эрхгүй бүртгэлийг
                  ! НЭГ ИЖИЛ 401-ээр хариулдаг (аль нь болохыг хэлэхгүй — код таах халдлагад мэдээлэл өгөхгүй). Тиймээс
                  ! алдааны кодоор ялгахгүй, дүрмийг л урьдчилж хэлнэ (docs/08 §PIN-ээр бүрэн нэвтрэх, D5).
                */}
                {isCode ? <p className='text-muted-foreground text-xs'>{t('auth.pinLockHint')}</p> : null}
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name='rememberMe'
            render={({ field }) => (
              <FormItem className='flex flex-row items-center gap-2'>
                <FormControl>
                  <Checkbox checked={field.value} onCheckedChange={checked => field.onChange(checked === true)} />
                </FormControl>
                <FormLabel className='text-sm font-normal'>{t('auth.rememberMe')}</FormLabel>
              </FormItem>
            )}
          />

          <Button type='submit' size='lg' disabled={isPending || redirecting || hasTo} className='mt-2 w-full'>
            {t(isPending || redirecting ? 'auth.signingIn' : 'auth.signIn')}
          </Button>
        </form>
      </Form>
    </AuthLayout>
  )
}

export default SignInView
